/**
 * A merch product chosen by its options, line by line: the model both
 * purchase sheets (Link in Bio's and the app's) draw. Each line holds one
 * value of every option (a size, a badge's type) and a quantity; the lines
 * together are the vendor selection, one unit count per ticket type.
 *
 * The options come from the store, structured, end to end: the journey's
 * `optionNames` and each ticket's `options`, never a ticket's name taken
 * apart. A journey without them, or with ones that do not fit together, has
 * no option set: the sheet lists its ticket types as before.
 */

import { optionKeyOf } from "@libs/buy-on-behalf-contracts/variants";
import { sellerLineMost } from "@libs/platform-wire";

/** One ticket type, as the options choose it. */
export type OptionChoice = {
  /** The journey ticket's id: the selection's `item_id`. */
  id: string;
  /** One value per option, in the options' order. */
  options: readonly string[];
  /** On sale now. One that is not shows, disabled, in the dropdowns. */
  available: boolean;
  minQuantity: number;
  /** Its seller's limit or stock; Infinity where the seller states none. */
  maxQuantity: number;
};

/** What a product is chosen by: its options' names, each option's values
 *  in the store's order, and the ticket type each combination is. */
export type OptionSet = {
  names: readonly string[];
  /** The item each option belongs to ("T-Shirt (TOUR)", "Badge"), in the
   *  options' order; null for a listing read before items were stored. */
  parts: readonly string[] | null;
  values: readonly (readonly string[])[];
  choices: readonly OptionChoice[];
};

/** How a dropdown is labelled: its item and its option ("T-Shirt (TOUR)",
 *  "Size", drawn "**T-Shirt (TOUR)** · Size"); no item for a listing read
 *  before items were stored. */
export const optionLabelOf = (set: OptionSet, option: number): { part: string | null; name: string } => ({
  part: set.parts?.[option] ?? null,
  name: set.names[option]!,
});

/** One line of the order: a value of each option, and how many. */
export type OptionLine = { key: string; values: readonly string[]; quantity: number };

/** A dropdown's entry: a value, and whether it can be chosen on its line. */
export type OptionValueState = { value: string; available: boolean };

type JourneyLike = {
  optionNames?: readonly string[] | null;
  optionParts?: readonly string[] | null;
  tickets: readonly {
    id: string;
    options?: readonly string[] | null;
    available?: boolean;
    minQuantity: number;
    /** Absent or null: no limit of the seller's (`sellerLineMost`). */
    maxQuantity?: number | null;
  }[];
};

const comboKey = (values: readonly string[]) => JSON.stringify(values);

export { optionKeyOf };

/**
 * The journey's option set: null when it has no options, or when they do
 * not fit together (a ticket without one value per option, two tickets with
 * the same values), so the sheet lists the ticket types instead.
 */
export function optionSetOf(journey: JourneyLike): OptionSet | null {
  const names = journey.optionNames;
  if (!names?.length || names.some((name) => !name.trim())) return null;
  // Each option once, by its item and name: a bundle's T-shirt and hoodie
  // may both be chosen by "Size".
  const parts = journey.optionParts ?? null;
  if (parts && (parts.length !== names.length || parts.some((part) => !part.trim()))) return null;
  if (new Set(names.map((name, index) => optionKeyOf(parts?.[index], name))).size !== names.length) return null;
  const seen = new Set<string>();
  const choices: OptionChoice[] = [];
  for (const ticket of journey.tickets) {
    const options = ticket.options;
    if (!options || options.length !== names.length || options.some((value) => !value.trim())) return null;
    const key = comboKey(options);
    if (seen.has(key)) return null;
    seen.add(key);
    choices.push({
      id: ticket.id,
      options,
      available: ticket.available !== false,
      minQuantity: ticket.minQuantity,
      maxQuantity: sellerLineMost(ticket),
    });
  }
  if (!choices.some((choice) => choice.available)) return null;
  const values = names.map((_, index) => {
    const own: string[] = [];
    for (const choice of choices) if (!own.includes(choice.options[index]!)) own.push(choice.options[index]!);
    return own;
  });
  return { names, parts, values, choices };
}

/** The ticket type a line's values make, if the product has it. */
export const choiceOf = (set: OptionSet, values: readonly string[]): OptionChoice | null =>
  set.choices.find((choice) => comboKey(choice.options) === comboKey(values)) ?? null;

const matches = (choice: OptionChoice, values: readonly string[], upTo: number) =>
  values.slice(0, upTo).every((value, index) => choice.options[index] === value);

/**
 * Each value of an option as its line's dropdown offers it, the options
 * chosen in order: a value can be chosen when a ticket type on sale has it
 * with the line's values of the options before it (the first option's,
 * with any). The later options then follow (`chooseValue`), so a value on
 * sale is never out of reach.
 */
export function valueStates(set: OptionSet, line: Pick<OptionLine, "values">, option: number): OptionValueState[] {
  return set.values[option]!.map((value) => ({
    value,
    available: set.choices.some(
      (choice) => choice.available && choice.options[option] === value && matches(choice, line.values, option),
    ),
  }));
}

/** How many units the order may still take outside a line: the most per
 *  order less every other line. */
export function roomFor(lines: readonly OptionLine[], maxOrderQuantity: number | null | undefined, key?: string): number {
  if (maxOrderQuantity === null || maxOrderQuantity === undefined) return Infinity;
  return maxOrderQuantity - lines.reduce((sum, line) => sum + (line.key === key ? 0 : line.quantity), 0);
}

/** The most a line may hold: its ticket type's limit, and the order's room. */
export function lineMax(set: OptionSet, lines: readonly OptionLine[], line: OptionLine, maxOrderQuantity?: number | null) {
  const choice = choiceOf(set, line.values);
  return Math.min(choice?.maxQuantity ?? 0, roomFor(lines, maxOrderQuantity, line.key));
}

const nextKey = (lines: readonly OptionLine[]) =>
  `line-${lines.reduce((most, line) => Math.max(most, Number(line.key.slice(5)) || 0), 0) + 1}`;

/** The first ticket type on sale not already on a line. */
const firstFree = (set: OptionSet, lines: readonly OptionLine[], maxOrderQuantity?: number | null) =>
  set.choices.find(
    (choice) => choice.available && choice.minQuantity <= roomFor(lines, maxOrderQuantity) &&
      !lines.some((line) => comboKey(line.values) === comboKey(choice.options)),
  ) ?? null;

/** The sheet's opening: one line of the first ticket type on sale, as many
 *  as it is sold in (none when the order cannot take them). */
export function openingLines(set: OptionSet, maxOrderQuantity?: number | null): OptionLine[] {
  const first = firstFree(set, [], maxOrderQuantity);
  if (!first) return [];
  return [{ key: "line-1", values: first.options, quantity: first.minQuantity }];
}

/**
 * The lines the product as read again still sells: a line whose ticket type
 * is gone or sold out leaves, and one over its type's limit or the order's
 * room comes down to it (below its least, it leaves). The same lines when
 * nothing changed.
 */
export function heldLines(set: OptionSet, lines: readonly OptionLine[], maxOrderQuantity?: number | null): readonly OptionLine[] {
  let room = maxOrderQuantity ?? Infinity;
  const held = lines.flatMap((line) => {
    const choice = choiceOf(set, line.values);
    if (!choice?.available) return [];
    const quantity = Math.min(line.quantity, choice.maxQuantity, room);
    if (quantity <= 0 || quantity < choice.minQuantity) return [];
    room -= quantity;
    return [quantity === line.quantity ? line : { ...line, quantity }];
  });
  return held.length === lines.length && held.every((line, index) => line === lines[index]) ? lines : held;
}

/** Whether another line can be added: a ticket type on sale on no line,
 *  and room in the order for its least. */
export function canAddLine(set: OptionSet, lines: readonly OptionLine[], maxOrderQuantity?: number | null): boolean {
  return !!firstFree(set, lines, maxOrderQuantity);
}

/** Another line: the first ticket type on sale on no line, at its least. */
export function addLine(set: OptionSet, lines: readonly OptionLine[], maxOrderQuantity?: number | null): OptionLine[] {
  const free = firstFree(set, lines, maxOrderQuantity);
  if (!free) return [...lines];
  return [...lines, { key: nextKey(lines), values: free.options, quantity: free.minQuantity }];
}

/** A line taken off the order; the last stays. */
export const removeLine = (lines: readonly OptionLine[], key: string): OptionLine[] =>
  lines.length > 1 ? lines.filter((line) => line.key !== key) : [...lines];

/**
 * A line's quantity, within its ticket type's limits and the order's room:
 * never below its least (a line goes with Remove), and an increase that
 * does not fit stays put.
 */
export function setLineQuantity(
  set: OptionSet,
  lines: readonly OptionLine[],
  key: string,
  next: number,
  maxOrderQuantity?: number | null,
): OptionLine[] {
  return lines.map((line) => {
    if (line.key !== key) return line;
    const choice = choiceOf(set, line.values);
    if (!choice) return line;
    const most = lineMax(set, lines, line, maxOrderQuantity);
    const quantity = next > line.quantity ? (next <= most ? next : line.quantity) : Math.max(next, choice.minQuantity);
    return { ...line, quantity };
  });
}

/**
 * A value chosen on a line. The options after it keep their values where
 * a ticket type on sale still has them, and take the first on sale where
 * not. A line that lands on another line's ticket type joins it: one line,
 * their quantities together, up to its limit.
 */
export function chooseValue(
  set: OptionSet,
  lines: readonly OptionLine[],
  key: string,
  option: number,
  value: string,
  maxOrderQuantity?: number | null,
): OptionLine[] {
  const line = lines.find((candidate) => candidate.key === key);
  if (!line || line.values[option] === value) return [...lines];
  let values = line.values.map((current, index) => (index === option ? value : current));
  for (let index = option + 1; index < values.length; index++) {
    const onSale = (candidate: string) =>
      set.choices.some(
        (choice) => choice.available && choice.options[index] === candidate && matches(choice, values, index),
      );
    if (onSale(values[index]!)) continue;
    const first = set.values[index]!.find(onSale);
    if (first === undefined) return [...lines];
    values = values.map((current, at) => (at === index ? first : current));
  }
  const choice = choiceOf(set, values);
  if (!choice?.available) return [...lines];
  const other = lines.find((candidate) => candidate.key !== key && comboKey(candidate.values) === comboKey(values));
  if (other)
    return lines
      .filter((candidate) => candidate.key !== key)
      .map((candidate) =>
        candidate.key === other.key
          ? { ...candidate, quantity: Math.min(choice.maxQuantity, candidate.quantity + line.quantity) }
          : candidate,
      );
  const most = Math.min(choice.maxQuantity, roomFor(lines, maxOrderQuantity, key));
  if (choice.minQuantity > most) return [...lines];
  const quantity = Math.max(choice.minQuantity, Math.min(line.quantity, most));
  return lines.map((candidate) => (candidate.key === key ? { ...candidate, values, quantity } : candidate));
}

/** The lines as the selection's quantities, by ticket type. */
export function lineQuantities(set: OptionSet, lines: readonly OptionLine[]): Record<string, number> {
  const quantities: Record<string, number> = {};
  for (const line of lines) {
    const choice = choiceOf(set, line.values);
    if (choice?.available && line.quantity > 0)
      quantities[choice.id] = (quantities[choice.id] ?? 0) + line.quantity;
  }
  return quantities;
}
